import {
  createAppointmentWaitlistDraftIdempotencyKey,
  createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration,
  refreshAppointmentWaitlistAuthority,
  validateAppointmentWaitlistDraftForAction,
  type AppointmentWaitlistActionAuthority,
  validateAppointmentWaitlistDraft,
  validateRetiredGroupAppointmentWaitlistDraftForStorageMigration,
  type AppointmentWaitlistDraftV3,
} from "./appointmentWaitlistDraft";
import {
  isAppointmentWaitlistConflictPublicationCurrent,
  type AppointmentWaitlistConflictPublicationV1,
} from "./appointmentWaitlistConfiguration";

export interface AppointmentWaitlistSubmission {
  waitlistRequestId: string;
  draftId: string;
  sourceAppointmentDraftId: string;
  customerAccountId: string;
  idempotencyKey: string;
  submittedAtEpochMs: number;
  acceptedAtEpochMs: number;
}

export type AppointmentWaitlistProviderFailureReason =
  | "aborted"
  | "invalid-customer"
  | "invalid-draft"
  | "provider-failed"
  | "provider-response-invalid"
  | "provider-unavailable"
  | "stale-draft";

export type AppointmentWaitlistProviderResult =
  | {
      ok: true;
      submission: AppointmentWaitlistSubmission;
    }
  | {
      ok: false;
      reason: AppointmentWaitlistProviderFailureReason;
      retryable: boolean;
    };

export interface AppointmentWaitlistProvider {
  /**
   * Requests one customer-bound waitlist entry. Implementations must return
   * provider-authored evidence; callers may not infer success from completion
   * of the Promise.
   */
  submitWaitlistRequest(
    draft: AppointmentWaitlistDraftV3,
    options: {
      idempotencyKey: string;
      signal?: AbortSignal;
    },
  ): Promise<AppointmentWaitlistProviderResult>;
}

export type AppointmentWaitlistProviderTransport = (
  draft: AppointmentWaitlistDraftV3,
  options: {
    idempotencyKey: string;
    signal?: AbortSignal;
  },
) => Promise<unknown>;

export interface AppointmentWaitlistSubmissionAuthorityResolver {
  resolve(
    draft: AppointmentWaitlistDraftV3,
    options: { signal?: AbortSignal },
  ): Promise<{
    actionAuthority: AppointmentWaitlistActionAuthority;
    conflictPublication: AppointmentWaitlistConflictPublicationV1;
  } | null>;
}

function isCanonicalIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

/**
 * A retry of the exact customer-bound waitlist request reuses this key.
 * Providers must create at most one request for it and reject the same key
 * with a conflicting payload.
 */
export function createAppointmentWaitlistIdempotencyKey(
  draft: AppointmentWaitlistDraftV3,
  options?: { evaluatedAtEpochMs?: number },
) {
  void options;
  return createAppointmentWaitlistDraftIdempotencyKey(draft);
}

function validateAppointmentWaitlistProviderResultForMode(input: {
  draft: AppointmentWaitlistDraftV3;
  idempotencyKey: string;
  result: unknown;
  evaluatedAtEpochMs?: number;
  /** Local activation lower bound for a newly received provider result. */
  acceptedNotBeforeEpochMs?: number;
  expectedMode: AppointmentWaitlistDraftV3["appointmentDraft"]["mode"];
}): AppointmentWaitlistProviderResult {
  const evaluatedAtEpochMs = input.evaluatedAtEpochMs ?? Date.now();
  const draftValidation =
    input.expectedMode === "individual"
      ? validateAppointmentWaitlistDraft(input.draft)
      : validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(
          input.draft,
        );
  const expectedIdempotencyKey = draftValidation.valid
    ? input.expectedMode === "individual"
      ? createAppointmentWaitlistIdempotencyKey(draftValidation.draft)
      : createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration(
          draftValidation.draft,
        )
    : null;

  if (
    !draftValidation.valid ||
    draftValidation.draft.customerAccountId === null ||
    expectedIdempotencyKey === null ||
    input.idempotencyKey !== expectedIdempotencyKey
  ) {
    return {
      ok: false,
      reason: "invalid-draft",
      retryable: false,
    };
  }

  if (!input.result || typeof input.result !== "object") {
    return {
      ok: false,
      reason: "provider-response-invalid",
      retryable: false,
    };
  }

  const result = input.result as Record<string, unknown>;

  if (result.ok === false) {
    const retryabilityByReason = {
      aborted: true,
      "invalid-customer": false,
      "invalid-draft": false,
      "provider-failed": true,
      "provider-response-invalid": false,
      "provider-unavailable": true,
      "stale-draft": false,
    } satisfies Record<AppointmentWaitlistProviderFailureReason, boolean>;
    const reason = result.reason as AppointmentWaitlistProviderFailureReason;
    const expectedRetryable = retryabilityByReason[reason];

    return expectedRetryable !== undefined &&
      result.retryable === expectedRetryable
      ? {
          ok: false,
          reason,
          retryable: expectedRetryable,
        }
      : {
          ok: false,
          reason: "provider-response-invalid",
          retryable: false,
        };
  }

  if (
    result.ok !== true ||
    !result.submission ||
    typeof result.submission !== "object"
  ) {
    return {
      ok: false,
      reason: "provider-response-invalid",
      retryable: false,
    };
  }

  const submission = result.submission as Record<string, unknown>;
  const customerAccountId = draftValidation.draft.customerAccountId;
  const submittedAtEpochMs =
    typeof submission.submittedAtEpochMs === "number"
      ? submission.submittedAtEpochMs
      : input.acceptedNotBeforeEpochMs;
  const configuration = draftValidation.draft.configurationPublication;
  const timeEvidence =
    draftValidation.draft.timeProfessionalMutationEvidence;

  if (
    !isCanonicalIdentifier(submission.waitlistRequestId) ||
    !isCanonicalIdentifier(input.idempotencyKey) ||
    submission.idempotencyKey !== input.idempotencyKey ||
    submission.draftId !== draftValidation.draft.draftId ||
    submission.sourceAppointmentDraftId !==
      draftValidation.draft.appointmentDraft.draftId ||
    submission.customerAccountId !== customerAccountId ||
    typeof submittedAtEpochMs !== "number" ||
    !Number.isSafeInteger(submittedAtEpochMs) ||
    submittedAtEpochMs < configuration.generatedAtEpochMs ||
    submittedAtEpochMs >= configuration.expiresAtEpochMs ||
    (timeEvidence !== null &&
      (submittedAtEpochMs < timeEvidence.generatedAtEpochMs ||
        submittedAtEpochMs >= timeEvidence.expiresAtEpochMs)) ||
    typeof submission.acceptedAtEpochMs !== "number" ||
    !Number.isSafeInteger(submission.acceptedAtEpochMs) ||
    submission.acceptedAtEpochMs < 0 ||
    (input.acceptedNotBeforeEpochMs !== undefined &&
      (!Number.isSafeInteger(input.acceptedNotBeforeEpochMs) ||
        input.acceptedNotBeforeEpochMs < 0 ||
        submittedAtEpochMs < input.acceptedNotBeforeEpochMs)) ||
    submission.acceptedAtEpochMs < submittedAtEpochMs ||
    (evaluatedAtEpochMs !== undefined &&
      submission.acceptedAtEpochMs > evaluatedAtEpochMs + 5 * 60_000)
  ) {
    return {
      ok: false,
      reason: "provider-response-invalid",
      retryable: false,
    };
  }

  return {
    ok: true,
    submission: {
      waitlistRequestId: submission.waitlistRequestId,
      draftId: draftValidation.draft.draftId,
      sourceAppointmentDraftId: draftValidation.draft.appointmentDraft.draftId,
      customerAccountId,
      idempotencyKey: input.idempotencyKey,
      acceptedAtEpochMs: submission.acceptedAtEpochMs,
      submittedAtEpochMs,
    },
  };
}

export function validateAppointmentWaitlistProviderResult(input: {
  draft: AppointmentWaitlistDraftV3;
  idempotencyKey: string;
  result: unknown;
  evaluatedAtEpochMs?: number;
  acceptedNotBeforeEpochMs?: number;
}): AppointmentWaitlistProviderResult {
  return validateAppointmentWaitlistProviderResultForMode({
    ...input,
    expectedMode: "individual",
  });
}

export function validateRetiredGroupAppointmentWaitlistProviderResultForStorageMigration(
  input: {
    draft: AppointmentWaitlistDraftV3;
    idempotencyKey: string;
    result: unknown;
    evaluatedAtEpochMs?: number;
    acceptedNotBeforeEpochMs?: number;
  },
): AppointmentWaitlistProviderResult {
  return validateAppointmentWaitlistProviderResultForMode({
    ...input,
    expectedMode: "group",
  });
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

/**
 * Adapts an untrusted waitlist transport into the Booking Core provider
 * boundary. The adapter owns exact retry-key validation, abort handling,
 * exception normalization, response validation and defensive cloning.
 *
 * A future HTTP implementation should be connected through this function;
 * route clients must never consume a raw response or infer acceptance merely
 * because a request resolved successfully.
 */
export function createValidatedAppointmentWaitlistProvider(
  submit: AppointmentWaitlistProviderTransport,
  authorityResolver: AppointmentWaitlistSubmissionAuthorityResolver,
): AppointmentWaitlistProvider {
  return {
    async submitWaitlistRequest(draft, options) {
      const expectedIdempotencyKey =
        createAppointmentWaitlistIdempotencyKey(draft);

      if (
        expectedIdempotencyKey === null ||
        options.idempotencyKey !== expectedIdempotencyKey
      ) {
        return {
          ok: false,
          reason: "invalid-draft",
          retryable: false,
        };
      }

      if (options.signal?.aborted) {
        return {
          ok: false,
          reason: "aborted",
          retryable: true,
        };
      }

      try {
        const resolvedAuthority = await authorityResolver.resolve(draft, options);
        const submissionActivatedAtEpochMs = Date.now();
        const authority = resolvedAuthority?.actionAuthority;
        const active = authority
          ? validateAppointmentWaitlistDraftForAction(draft, {
              ...authority,
              evaluatedAtEpochMs: submissionActivatedAtEpochMs,
            })
          : null;
        const conflictIsCurrent =
          active?.valid === true &&
          resolvedAuthority?.conflictPublication.result.kind === "none" &&
          isAppointmentWaitlistConflictPublicationCurrent({
            publication: resolvedAuthority.conflictPublication,
            configuration: authority!.currentConfigurationPublication,
            preferences: active.draft.preferences,
            expectedProviderIdentity:
              authority!.expectedConfigurationProviderIdentity,
            evaluatedAtEpochMs: submissionActivatedAtEpochMs,
          });
        const authorizedDraft =
          active?.valid && conflictIsCurrent
            ? refreshAppointmentWaitlistAuthority({
                draft: active.draft,
                configurationPublication:
                  authority!.currentConfigurationPublication,
                conflictPublication: resolvedAuthority!.conflictPublication,
                actionAuthority: {
                  ...authority!,
                  evaluatedAtEpochMs: submissionActivatedAtEpochMs,
                },
              })
            : null;
        if (
          options.signal?.aborted ||
          !authorizedDraft ||
          authorizedDraft.stage !== "confirmation" ||
          authorizedDraft.customerAccountId === null ||
          !authorizedDraft.confirmation
        ) {
          return {
            ok: false,
            reason: options.signal?.aborted ? "aborted" : "stale-draft",
            retryable: options.signal?.aborted === true,
          };
        }
        const result = await submit(authorizedDraft, options);
        // The transport acceptance time is provider-authored. The mutation
        // activation is local evidence frozen at the final authority boundary.
        // Preserve that activation through later attempt/receipt validation.
        const normalizedResult =
          result &&
          typeof result === "object" &&
          "ok" in result &&
          result.ok === true &&
          "submission" in result &&
          result.submission &&
          typeof result.submission === "object"
            ? {
                ...result,
                submission: {
                  ...result.submission,
                  submittedAtEpochMs: submissionActivatedAtEpochMs,
                },
              }
            : result;

        const validated = validateAppointmentWaitlistProviderResult({
          draft: authorizedDraft,
          idempotencyKey: options.idempotencyKey,
          result: normalizedResult,
          acceptedNotBeforeEpochMs: submissionActivatedAtEpochMs,
          evaluatedAtEpochMs: Date.now(),
        });
        return options.signal?.aborted && !validated.ok
          ? { ok: false, reason: "aborted", retryable: true }
          : validated;
      } catch (error) {
        return {
          ok: false,
          reason:
            options.signal?.aborted || isAbortError(error)
              ? "aborted"
              : "provider-failed",
          retryable: true,
        };
      }
    },
  };
}
